import * as React from "react";
import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
} from "react-router-dom";
import { SWRConfig } from "swr";
import useSWR from "swr";
import { localAxios, logPageVisit } from "./api";

import SelectLocation from './pages/SelectLocation';
import Acknowledge, { loader as acknowledgeLoader } from './pages/acknowledge/Acknowledge';
import Approve, { loader as approveLoader } from './pages/approve/Approve';
import HomePage, { loader as homePageLoader } from './pages/HomePage';
import RegisterPage from './pages/RegisterPage';
import RegisterVisitor from './pages/register-visitor/RegisterVisitor';
import Success from './pages/Success';
import Approved from './pages/Approved';
import VisitSubmitted from './pages/VisitSubmitted';
import NotFoundPage from './pages/NotFoundPage';
import Login from './auth/Login';
import Reports from './pages/Reports';
import Administration from './pages/Administration';
import Billing from './pages/Billing';
import AccountPage from './pages/AccountPage';
import Help from './pages/Help';
import GuardianConfirm from './pages/GuardianConfirm';
import VisitorConfirm from './pages/VisitorConfirm';

function ProtectedRoute({ children }) {
  const { data, isLoading } = useSWR('/api/me', localAxios);
  if (isLoading) return null;
  if (!data?.data?.loggedIn) {
    const path = window.location.pathname;
    if (path && path !== '/login') localStorage.setItem('redirectPath', path);
    return <Navigate to="/login" />;
  }
  return children;
}

// pageName is a vtt.page.page_name slug. accessible_pages comes from /api/me and lists
// the pages the user's security groups grant can_view on. The API routes behind these
// pages enforce the same check server-side; this is only for UX.
function PagePermissionRoute({ pageName, children }) {
  const { data, isLoading } = useSWR('/api/me', localAxios);
  if (isLoading) return null;
  if (!data?.data?.accessible_pages?.includes(pageName)) return <Navigate to="/" />;
  return children;
}

const router = createBrowserRouter([
  {
    path: '/',
    element: <ProtectedRoute><HomePage /></ProtectedRoute>,
    loader: homePageLoader,
    errorElement: <NotFoundPage />,
  },
  {
    path: '/login',
    element: <Login />,
    errorElement: <NotFoundPage />,
  },
  {
    path: '/register',
    element: <RegisterPage />,
    errorElement: <NotFoundPage />,
  },
  {
    path: '/help',
    element: <Help />,
    errorElement: <NotFoundPage />,
  },
  {
    path: '/register-visitor',
    element: <ProtectedRoute><RegisterVisitor /></ProtectedRoute>,
    errorElement: <NotFoundPage />,
  },
  {
    path: '/acknowledge/:siteName',
    element: <ProtectedRoute><Acknowledge /></ProtectedRoute>,
    loader: acknowledgeLoader,
    errorElement: <NotFoundPage />,
  },
  {
    path: '/approve/:siteName',
    element: <ProtectedRoute><Approve /></ProtectedRoute>,
    loader: approveLoader,
    errorElement: <NotFoundPage />,
  },
  {
    path: '/success',
    element: <ProtectedRoute><Success /></ProtectedRoute>,
    errorElement: <NotFoundPage />,
  },
  {
    path: '/approved',
    element: <ProtectedRoute><Approved /></ProtectedRoute>,
    errorElement: <NotFoundPage />,
  },
  {
    path: '/choose',
    element: <ProtectedRoute><SelectLocation /></ProtectedRoute>,
    errorElement: <NotFoundPage />,
  },
  {
    path: '/visit-submitted',
    element: <ProtectedRoute><VisitSubmitted /></ProtectedRoute>,
    errorElement: <NotFoundPage />,
  },
  {
    path: '/reports',
    element: <ProtectedRoute><PagePermissionRoute pageName="reports"><Reports /></PagePermissionRoute></ProtectedRoute>,
    errorElement: <NotFoundPage />,
  },
  {
    path: '/admin',
    element: <ProtectedRoute><PagePermissionRoute pageName="admin"><Administration /></PagePermissionRoute></ProtectedRoute>,
    errorElement: <NotFoundPage />,
  },
  {
    path: '/billing',
    element: <ProtectedRoute><PagePermissionRoute pageName="billing"><Billing /></PagePermissionRoute></ProtectedRoute>,
    errorElement: <NotFoundPage />,
  },
  {
    path: '/account',
    element: <ProtectedRoute><AccountPage /></ProtectedRoute>,
    errorElement: <NotFoundPage />,
  },
  {
    path: '/guardian-confirm',
    element: <ProtectedRoute><GuardianConfirm /></ProtectedRoute>,
    errorElement: <NotFoundPage />,
  },
  {
    path: '/visitor-confirm',
    element: <ProtectedRoute><VisitorConfirm /></ProtectedRoute>,
    errorElement: <NotFoundPage />,
  },
]);

let previousPath = null;
let isInitialNavigation = true;

router.subscribe((state) => {
  const currentPath = state.location.pathname + state.location.search;
  let referrerPath;
  let navSource;

  if (isInitialNavigation) {
    isInitialNavigation = false;
    referrerPath = document.referrer || null;
    if (!document.referrer) {
      navSource = 'direct link';
    } else {
      try {
        navSource = new URL(document.referrer).origin === window.location.origin
          ? 'internal navigation'
          : 'external link';
      } catch {
        navSource = 'external link';
      }
    }
  } else {
    referrerPath = previousPath;
    navSource = state.historyAction === 'POP' ? 'back/forward'
      : state.historyAction === 'REPLACE' ? 'redirect'
      : 'internal navigation';
  }

  previousPath = currentPath;
  logPageVisit(currentPath, referrerPath, navSource);
});

function App() {
  return (
    <SWRConfig value={{ fetcher: localAxios, revalidateOnMount: true, shouldRetryOnError: false }}>
      <div>
        <RouterProvider router={router} />
      </div>
    </SWRConfig>
  );
}

export default App;
